/* eslint-disable import/order */
/* eslint-disable react-hooks/exhaustive-deps */

import { Input, Switch, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { Dayjs } from 'dayjs';
import { isPossiblePhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import _, { concat, get, set } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router';
import {
  CountryInput,
  DateInput,
  DocumentInput,
  EmailFormItem,
  EmirateSelect,
  NationalitySelect,
  PhoneNumberFormItem,
} from '~/components';
import SubmitConfirmationModal from '~/components/SubmitConfirmationModal';
import Form from '~/components/WizardForm';
import CardEmbossingInput from '~/components/inputs/CardEmbossingInput';
import EmiratesIdFormInput from '~/components/inputs/EmiratesIdFormItem';
import SalutationDropdown from '~/components/inputs/SalutationDropdown';
import { useCardHolderContext } from '~/controllers/cardHolderContext';
import { useSaveCardHolderDetails } from '~/controllers/cardholder';
import { useCheckMatchingContact, useMutateContactsApi } from '~/controllers/contacts';
import i18n from '~/i18n';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import paths from '~/pages/paths';
import { setCardholderData } from '~/slices/state';
import { Contacts } from '~/types/Contacts';
import { addressValidator } from '~/utils/addressValidation';
import { nameValidatorRules } from '~/utils/basicValidation';
import { contactObjCreation } from '~/utils/contacts';
import createUid from '~/utils/createUid';
import { disabledDate18Plus, disabledDateIfLesserThanGivenNumberOfDays } from '~/utils/disableDateRule';
import emailDuplicateCheck from '~/utils/emailValidation';
import { isSales } from '~/utils/getDecodedJwt';
import { validatePassportNumber } from '~/utils/passportValidation';
import phoneValidation from '~/utils/phoneValidation';
import ContactsDropDown from '~/components/ContactsDropDown';

const CardHolderDetails: any = (props?: any) => {
  const { t } = useTranslation('cardholder');
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [isResidentialSameAsPostal, setIsResidentialSameAsPostal] = useState(false);
  const history = useHistory();
  const sales = isSales();
  const {
    onFinish,
    initialValues,
    setMatchingContactList,
    setSelectedContact,
    setModalOpen,
    handleMatchingModalAsync,
    isAdding,
    cardHolderType,
  } = props;
  const formName = sales ? 'addCardholder' : '/cardHolder/details';
  const newUid = createUid();
  const { t: tCommon } = useTranslation('common');

  const { updateStepKey, cardHolderData, updateCardHolderData } = useCardHolderContext();
  const isOnboardingCompleted = cardHolderData && cardHolderData.status === 'RECEIVED_CONSENT';
  const isPersonalDetailsFilled = cardHolderData && cardHolderData.status === 'CONSENT_NOT_RECEIVED';

  const saveCardHolderMutation = useSaveCardHolderDetails();
  const contactMutation = useMutateContactsApi();

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isCopyingProfile, setIsCopyingProfile] = useState(false);

  useEffect(() => {
    if (isAdding && !_.isEmpty(cardHolderData)) {
      form.setFieldsValue({
        ...cardHolderData,
      });
    }
  }, [cardHolderData, form, isAdding]);

  const onValuesChange = (_changedValues: any, allValues: any) => {
    form.setFieldsValue({
      ...allValues,
      residentialAddress: isResidentialSameAsPostal ? allValues.mailingAddress : allValues.residentialAddress,
    });
    dispatch(setCardholderData(allValues));
  };

  useEffect(() => {
    if (isPersonalDetailsFilled) {
      updateStepKey('consent');
      history.push(paths.cardHolder.consent);
    }
  }, [history, isPersonalDetailsFilled, updateStepKey]);

  useEffect(() => {
    const allValues = form.getFieldsValue();
    if (allValues && allValues.mailingAddress) {
      form.setFieldsValue({
        ...allValues,
        residentialAddress: isResidentialSameAsPostal ? allValues.mailingAddress : allValues.residentialAddress,
      });
    }
  }, [form, isResidentialSameAsPostal]);

  const handleOnclick = (values: any) => {
    setTimeout(() => {
      const isFieldsHaveErrors = !!form.getFieldsError()?.find((item) => item.errors.length);
      if (!isFieldsHaveErrors) {
        if (isOnboardingCompleted) {
          updateStepKey('consent');
          history.push(paths.cardHolder.consent);
        } else {
          setIsFormSubmitted(true);
        }
      }
    }, 0);
  };

  const submitForm = async () => {
    if (!isOnboardingCompleted) {
      try {
        const response = await saveCardHolderMutation.mutateAsync({ ...cardHolderData, ...form.getFieldsValue() });
        updateCardHolderData(response);
        history.push({
          pathname: paths.cardHolder.consent,
          // state: { formData: { ...(cardHolderData && cardHolderData[0]), ...form.getFieldsValue() } },
        });
        updateStepKey('consent'); // Change it new step key
      } catch (err) {
        console.error(err);
      }
    }
  };

  const checkMatchingContact = useCheckMatchingContact();

  const submitSalesStaffForm = async (value: any) => {
    try {
      if (isAdding && !isCopyingProfile) {
        const matchingContactList = await checkMatchingContact.mutateAsync(value);
        const matchType = matchingContactList[0]?.matchType;
        const contactIdList = matchingContactList.map((contact: Contacts['contacts']) => contact.contactId);
        const contactUserRolesList = matchingContactList.map((contact: Contacts['contacts']) => contact.userRoles);

        switch (matchType) {
          case 'FULL_MATCH':
            set(value, 'contactId', contactIdList[0]);
            set(value, 'matchContactId', contactIdList[0]);
            set(value, 'userRoles', concat(value.userRoles, contactUserRolesList[0]));
            break;
          case 'PARTIAL_MATCH': {
            setMatchingContactList(matchingContactList);
            setSelectedContact(undefined);
            setModalOpen(true);
            const result = await handleMatchingModalAsync();
            if (result.choice === 'select' && result.selectedContact) {
              set(value, 'userRoles', concat(value.userRoles, result.selectedContact.userRoles));
              set(value, 'contactId', result.selectedContact.contactId);
              set(value, 'matchContactId', result.selectedContact.contactId);
            }
            break;
          }
          default:
        }
      }
      // const response = saveCardHolderMutation.mutateAsync({ ...cardHolderData, ...form.getFieldsValue() });
      updateCardHolderData(value);
      contactMutation.mutate(
        contactObjCreation(
          { ...value, contactId: value?.contactId ?? undefined },
          { type: 'ADD', role: cardHolderType === 'CORPORATE_EXECUTIVE_CARD' ? 'cardholder' : 'businessCardholder' },
        ),
      );
      onFinish(value);
    } catch (err) {
      console.error(err);
    }
  };

  const onFormFinish = sales ? (value: any) => submitSalesStaffForm(value) : (value: any) => handleOnclick(value);

  // soft rejection comparing data

  useEffect(() => {
    dispatch(setCardholderData(cardHolderData));
  }, [cardHolderData]);

  //
  if (isOnboardingCompleted) {
    return <Redirect to="/cardHolder/completion" />;
  }
  if (isPersonalDetailsFilled) {
    return <Redirect to="/cardHolder/consent" />;
  }

  return (
    <Container>
      <Form
        form={form}
        stepKey="addresses"
        onValuesChange={onValuesChange}
        onFinish={onFormFinish}
        onSubmitCapture={(value) => handleOnclick(value)}
        name={formName}
        initialValues={initialValues}
        // initialValues={{
        //   city: 'Ajman',
        //   name: 'AAA BBB',
        //   email: 'aaabbb@yopmail.com',
        //   mobile: '+971551236543',
        //   country: 'United Arab Emirates',
        //   lastName: 'BBB',
        //   passport: {
        //     files: [
        //       {
        //         id: 'MTcyM2MwMDUtNTFiYi00MWVlLWI4ZDUtZDcyNDFlYmZhZDgzOGM4MmI5NzctODQ1OS00Zjk1LWEzYjQtY2E2NGI0MTcyYzhjP2FjY2Vzc0tleT05NDliMzRlNTBhOGJiNWUyMTg1MjM1ODkyYmFkOWQ3YTQyMmU0MjE2Nzc0NWYyMTlhZmVjZjA2MDU2MWIxZmMy',
        //         mimeType: 'image/png',
        //         container: 'sob-ni-1817',
        //       },
        //     ],
        //     documentNumber: '',
        //     expiryDate: '2026-01-01',
        //   },
        //   firstName: 'AAA',
        //   workPhone: '+971551236543',
        //   emiratesId: {
        //     files: [
        //       {
        //         id: 'MTcyM2MwMDUtNTFiYi00MWVlLWI4ZDUtZDcyNDFlYmZhZDgzMDc2MDJlMjgtMmZlZS00YTRiLTk0YmEtYWNlZGE2NDlmZDY0P2FjY2Vzc0tleT02NTg4NzI1OWM5MzdkZjg3ZDFjZWMwMjYyYjIxY2I0YzFhYjMzNjFlY2U0OWM1OTdkM2Q2MDNlMmZmYzJhMzRl',
        //         mimeType: 'image/png',
        //         container: 'sob-ni-1817',
        //       },
        //     ],
        //     documentNumber: '784-1986-1234567-2',
        //     expiryDate: '2026-01-01',
        //   },
        //   salutation: 'MR',
        //   dateOfBirth: '1990-01-01',
        //   designation: 'manager',
        //   nationality: 'UAE',
        //   mailingAddress: 'aaaaaaa',
        //   motherMaidenName: 'cccc',
        //   cardEmbossingLine1: 'aaaaa bbbb',
        //   cardEmbossingLine2: 'aaaaaa',
        //   residentialAddress: 'bbbbbb',
        // }}
      >
        <span style={{ display: 'none' }}>
          {' '}
          <Form.Item name={['id']} hidden />{' '}
        </span>
        {isAdding && (
          <ContactsDropDown
            excludedRoles={cardHolderType === 'CORPORATE_EXECUTIVE_CARD' ? 'cardholder' : 'businessCardholder'}
            onSelect={(contact) => {
              setIsCopyingProfile(true);
              form.setFieldsValue(contact);
              form.setFieldValue(
                'userRoles',
                concat(
                  contact.userRoles,
                  cardHolderType === 'CORPORATE_EXECUTIVE_CARD' ? 'cardholder' : 'businessCardholder',
                ),
              );
              dispatch(setCardholderData(contact));
            }}
          />
        )}
        <Typography.Title>{t('cardholder.details.title')}</Typography.Title>
        <Typography.Paragraph className="Cardholder-Title">{t('cardholder.details.description')}</Typography.Paragraph>
        <Section formItemLast>
          <Typography.Title level={4}>{t('cardholder.details.personal_data').toUpperCase()}</Typography.Title>
          {sales && (
            <Form.Item
              hidden
              name={['contactId']}
              initialValue={get(initialValues, ['cardHolderValues', 'contactId']) || newUid}
            />
          )}
          <Form.Item
            initialValue={
              get(initialValues?.cardHolderValues, ['userRoles']) ||
              (cardHolderType === 'CORPORATE_EXECUTIVE_CARD' ? ['cardholder'] : ['businessCardholder'])
            }
            hidden
            name={['userRoles']}
          />
          <InputsGroupLayout>
            <Form.Item
              rules={[{ required: true }]}
              label={t('cardholder.details.personal_data.salutation')}
              name={['salutation']}
            >
              <SalutationDropdown />
            </Form.Item>
            <Form.Item
              disabled={!sales}
              rules={[...nameValidatorRules, { required: true }]}
              label={t('cardholder.details.personal_data.first_name')}
              name={['firstName']}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('cardholder.details.personal_data.second_name')}
              name={['secondName']}
              rules={nameValidatorRules}
            >
              <Input />
            </Form.Item>
            <Form.Item
              rules={[...nameValidatorRules, { required: true }]}
              label={t('cardholder.details.personal_data.last_name')}
              name={['lastName']}
              disabled={!sales}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('cardholder.details.personal_data.mother_maiden_name')}
              name="motherMaidenName"
              rules={[...nameValidatorRules, { required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              rules={[{ required: true }]}
              label={t('cardholder.details.personal_data.nationality')}
              name={['nationality']}
            >
              <NationalitySelect />
            </Form.Item>
            <Form.Item
              rules={[{ required: true }]}
              label={t('cardholder.details.personal_data.dob')}
              name={['dateOfBirth']}
              help={t('cardholder.details.personal_data.dob.help')}
            >
              <DateInput disableDateRule={disabledDate18Plus} />
            </Form.Item>
            <PhoneNumberFormItem
              label={t('cardholder.details.personal_data.phone')}
              name={['mobileNo']}
              rules={[
                { required: sales },
                {
                  validator: async (rule, value) => {
                    if (!value.startsWith('+971')) {
                      throw new Error(
                        i18n.t('Phone number must be in UAE international format (+971)', { ns: 'validation' }),
                      );
                    }
                    const formatedValue = value.startsWith('+0') ? value.replaceAll('+0', '971') : value;
                    const phoneNumber = parsePhoneNumberFromString(formatedValue, 'AE');
                    const number: any = phoneNumber?.number;
                    if (!isPossiblePhoneNumber(number, phoneNumber?.country)) {
                      return Promise.reject(new Error(i18n.t('Impossible number', { ns: 'validation' })));
                    }
                    if (!phoneNumber?.isValid()) {
                      return Promise.reject(new Error(i18n.t('Invalid number', { ns: 'validation' })));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              disabled={!sales}
            />
            <PhoneNumberFormItem
              label={t('cardholder.details.personal_data.work_phone')}
              name={['phoneNo']}
              rules={[
                { required: true },
                {
                  validator: phoneValidation(),
                },
              ]}
            />
            {sales && (
              <>
                <Form.Item label={t('Designation')} name={['designation']} rules={[{ required: true }]}>
                  <Input />
                </Form.Item>
                <EmailFormItem
                  name={['email']}
                  rules={[
                    { required: sales },
                    { type: 'email', message: tCommon('Invalid email', { ns: 'validation' }) },
                    { max: 40, message: tCommon('Email must be less than 40 characters', { ns: 'validation' }) },
                    {
                      validator: emailDuplicateCheck(
                        form.getFieldValue(['contactId']),
                        cardHolderType === 'CORPORATE_EXECUTIVE_CARD' ? 'cardholder' : 'businessCardholder',
                      ),
                    },
                  ]}
                />
              </>
            )}
          </InputsGroupLayout>
        </Section>
        <br />

        {/* Documents Section */}
        <Section>
          <Typography.Title level={4}>{t('cardholder.details.personal_data.documents')}</Typography.Title>

          {/* Passport - Details */}
          <Typography.Title level={5}>{t('cardholder.details.personal_data.passport')}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item
              rules={[{ required: true }, { validator: validatePassportNumber() }]}
              validateFirst
              label={t('cardholder.details.personal_data.passport.document_number')}
              name={['passport', 'documentNumber']}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label={t('cardholder.details.personal_data.passport.expiry_date')}
              name={['passport', 'expiryDate']}
              rules={[{ required: true }]}
            >
              <DateInput disableDateRule={(current: Dayjs) => disabledDateIfLesserThanGivenNumberOfDays(current, 3)} />
            </Form.Item>
          </InputsGroupLayout>
          <Form.Item name={['passport', 'files']} rules={[{ required: true }]}>
            <DocumentInput name="document" variant="default" />
          </Form.Item>

          {/* Emirates Id - Details */}
          <Typography.Title level={5}>{`${t('cardholder.details.personal_data.emirates_id')}`}</Typography.Title>
          <InputsGroupLayout>
            <EmiratesIdFormInput
              label={t('cardholder.details.personal_data.emirates_id.document_number')}
              name={['emiratesId', 'documentNumber']}
              isRequired
            />
            <Form.Item
              label={t('cardholder.details.personal_data.emirates_id.expiry_date')}
              name={['emiratesId', 'expiryDate']}
              rules={[{ required: true }]}
            >
              <DateInput disabledDate={(current: Dayjs) => disabledDateIfLesserThanGivenNumberOfDays(current, 3)} />
            </Form.Item>
          </InputsGroupLayout>
          <Form.Item name={['emiratesId', 'files']} rules={[{ required: true }]}>
            <DocumentInput name="document" variant="default" optional={false} />
          </Form.Item>

          {/* Visa - Details */}
          {/* {nationality !== 'UAE' && (
            <>
              <Typography.Title level={5}>{t('cardholder.details.personal_data.visa')}</Typography.Title>
              <InputsGroupLayout>
                <Form.Item
                  label={t('cardholder.details.personal_data.visa.expiry_date')}
                  name={['visa', 'expiryDate']}
                  rules={[{ required: true }]}
                >
                  <DateInput
                    disableDateRule={(current: Dayjs) => disabledDateIfLesserThanGivenNumberOfDays(current, 3)}
                  />
                </Form.Item>
              </InputsGroupLayout>
            </>
          )} */}
        </Section>
        {/* Address Related Details - Postal */}
        <Section>
          <Typography.Title level={4}>{t('cardholder.details.personal_data.address').toUpperCase()}</Typography.Title>

          {/* /// */}
          {/* <Typography.Title level={5}>{t('Country')}</Typography.Title> */}
          <InputsGroupLayout>
            <Form.Item
              label="Country"
              rules={[{ required: true }]}
              key="country-FormItem"
              name={['country']}
              style={{ marginBottom: '10px', width: '100%' }}
              initialValue="United Arab Emirates"
              disabled
            >
              <CountryInput />
            </Form.Item>
            <Form.Item rules={[{ required: true }]} label={t('cardholder.details.personal_data.city')} name={['city']}>
              <EmirateSelect />
            </Form.Item>
          </InputsGroupLayout>

          {/*  */}
          <Typography.Title level={5}>{t('cardholder.details.personal_data.postal_address')}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item
              rules={[
                { required: true },
                {
                  validator: addressValidator,
                },
              ]}
              label={t('cardholder.details.personal_data.address_line')}
              name={['mailingAddress']}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>

          {/* Address - Residential */}
          <div className="cardholder-address-switch-container">
            <Switch
              disabled={isOnboardingCompleted}
              style={{ marginRight: '10px' }}
              onChange={() => {
                const newIsResidentialSameAsPostal = !isResidentialSameAsPostal;
                setIsResidentialSameAsPostal(newIsResidentialSameAsPostal);
                form.setFieldsValue({
                  cardHolderDetails: {
                    usePostalAddressAsResidential: newIsResidentialSameAsPostal,
                  },
                });
              }}
              checked={isResidentialSameAsPostal}
            />
            <span>{t('cardholder.details.personal_data.residential_switch')}</span>
            <span style={{ display: 'none' }}>
              {' '}
              <Form.Item name={['usePostalAddressAsResidential']} hidden />{' '}
            </span>
          </div>
          <Typography.Title level={5}>{t('cardholder.details.personal_data.residential_address')}</Typography.Title>
          <InputsGroupLayout>
            <Form.Item
              disabled={isResidentialSameAsPostal}
              rules={[
                { required: true },
                {
                  validator: addressValidator,
                },
              ]}
              label={t('cardholder.details.personal_data.address_line')}
              name={['residentialAddress']}
            >
              <Input />
            </Form.Item>
          </InputsGroupLayout>
        </Section>
        <Section>
          <Typography.Title level={4}>{t('cardholder.details.card_embossing_line').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <CardEmbossingInput
              isLine1
              label={t('cardholder.details.personal_data.card_embossing_line_1')}
              name={['cardEmbossingLine1']}
              helpText={t('cardholder.details.personal_data.card_embossing_line.validation')}
            />
            <CardEmbossingInput
              label={t('cardholder.details.personal_data.card_embossing_line_2')}
              name={['cardEmbossingLine2']}
              helpText={t('cardholder.details.personal_data.card_embossing_line.validation')}
            />
          </InputsGroupLayout>
        </Section>
        {!sales && (
          <SubmitConfirmationModal
            visible={isFormSubmitted}
            onSubmit={submitForm}
            onCancel={() => {
              setIsFormSubmitted(false);
            }}
          />
        )}
      </Form>
    </Container>
  );
};

const Container = styled.div`
  .cardholder-address-switch-container,
  .cardholder-address-deliver-address-container {
    margin: 15px 0px;
  }
`;

export default CardHolderDetails;
